import { useSearchParams } from "react-router-dom";
import { useMemo, useEffect } from "react";

/**
 * Parses widget URL params and applies partner branding (primary color via CSS var).
 * Returned object: { asset, amount, primaryColor, logo, userId, wallet, partnerId, walletLocked }
 */
export function useWidgetParams() {
  const [params] = useSearchParams();
  const cfg = useMemo(() => {
    const asset = (params.get("asset") || "").toUpperCase() || null;
    const amount = params.get("amount");
    const wallet = params.get("wallet") || null;
    return {
      asset,
      amount: amount && !isNaN(Number(amount)) ? Number(amount) : null,
      primaryColor: params.get("primaryColor") || null,
      logo: params.get("logo") || null,
      userId: params.get("user_id") || null,
      wallet,
      walletLocked: !!wallet,
      partnerId: params.get("partner_id") || null,
    };
  }, [params]);

  useEffect(() => {
    if (!cfg.primaryColor) return;
    // Apply primary CSS variable override globally for this session
    const hex = cfg.primaryColor.startsWith("#") ? cfg.primaryColor : `#${cfg.primaryColor}`;
    const hsl = hexToHsl(hex);
    if (hsl) {
      document.documentElement.style.setProperty("--primary", hsl);
      document.documentElement.style.setProperty("--accent", hsl);
      document.documentElement.style.setProperty("--ring", hsl);
    }
    return () => {
      // Don't remove — partner branding should persist within the session
    };
  }, [cfg.primaryColor]);

  return cfg;
}

function hexToHsl(hex) {
  const m = hex.replace("#", "").match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return null;
  let v = m[1];
  if (v.length === 3) v = v.split("").map((c) => c + c).join("");
  const r = parseInt(v.slice(0, 2), 16) / 255;
  const g = parseInt(v.slice(2, 4), 16) / 255;
  const b = parseInt(v.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
